import { SafePromise } from '@benzinga/safe-await';

import { RestfulClient } from '@benzinga/session';
import {
  NotificationHistoryResponse,
  NotificationHistoryParams,
  NotificationMarkReadUnreadParams,
  NotificationMarkReadUnreadResponse,
  NotificationReadUpdateParams,
  NotificationConfigParams,
  NotificationConfigSetResponse,
  NotificationConfigGetResponse,
  MarketNotificationFrequency,
  MarketNotificationFrequencyRes,
  GetMarketNotificationFrequencyResponse,
  UpdateGlobalNotificationSettings,
  FetchGlobalNotificationSettings,
  UpdateGlobalNotificationSettingsParams,
  EditorialMarketAlertsNotificationParams,
  SendGroupDataResponse,
  EditorialMarketAlertsNotificationResponse,
  EditorialMarketAlertsAnalyticsResponse,
  NotificationSettingAlertsParams,
  NotificationRegisterParams,
  NotificationRegisterResponse,
  RegisterTokenParams,
} from './entities';
import { Session } from '@benzinga/session';
import { NotificationEnvironment } from './environment';

export class NotificationRestful extends RestfulClient {
  constructor(session: Session) {
    super(session.getEnvironment(NotificationEnvironment).url, session, { 'x-device-key': true });
  }

  public registerToken = (params: RegisterTokenParams) => {
    const url = this.URL('/api/v1.1/notification/device');
    return this.post(url, params);
  };

  public getNotificationHistory = (params: NotificationHistoryParams): SafePromise<NotificationHistoryResponse> => {
    const url = this.URL(`/api/v1.1/notification/history`, params);
    return this.get(url, { allowsAnonymousAuth: true });
  };

  public getConfigurations = (): SafePromise<NotificationConfigGetResponse> => {
    const url = this.URL('/api/v1.1/notification/settings');
    return this.get(url);
  };

  public setConfigurations = (params: NotificationConfigParams): SafePromise<NotificationConfigSetResponse> => {
    const url = this.URL('api/v1.1/notification/settings/destinations');
    return this.patch(url, params);
  };

  public fetchGlobalNotificationSettings = (): SafePromise<FetchGlobalNotificationSettings> => {
    const url = this.URL('/api/v2/watchlists/realtime');
    return this.get(url);
  };

  public updateGlobalNotificationSettings = (
    params: UpdateGlobalNotificationSettingsParams,
  ): SafePromise<UpdateGlobalNotificationSettings> => {
    const url = this.URL('/api/v2/watchlists/realtime');
    return this.post(url, params);
  };

  public notificationSettingsAlerts = (params: NotificationSettingAlertsParams) => {
    const url = new URL(
      `/services/watchlist/bzuser/alert/${params.symbolId}`,
      this.session.getEnvironment(NotificationEnvironment).rootUrl,
    );
    const body = {
      is_pr: params.pr,
      is_sec: params.sec,
      send_realtime: params.sr,
    };
    const header = {
      'x-device-key': true,
    };
    return this.put(url, body, { includeHeader: header });
  };

  public getMarketNotificationFrequency = (): SafePromise<GetMarketNotificationFrequencyResponse> => {
    const url = this.URL('/api/v1/market-alerts/subscribe');
    return this.get(url);
  };

  public setMarketNotificationFrequency = (
    params: MarketNotificationFrequency,
  ): SafePromise<MarketNotificationFrequencyRes> => {
    const url = this.URL('/api/v1/market-alerts/subscribe');
    return this.patch(url, params);
  };

  public sendEditorialMarketAlertsNotification = (
    params: EditorialMarketAlertsNotificationParams,
  ): SafePromise<EditorialMarketAlertsNotificationResponse> => {
    const url = this.URL('/api/v1/market-alerts/editorial/push');
    return this.post(url, params);
  };

  public getMarketAlertsAnalytics = (): SafePromise<EditorialMarketAlertsAnalyticsResponse> => {
    const url = this.URL('/api/v1/market-alerts/list/analytics');
    return this.get(url);
  };

  public getSendGroupData = async (): SafePromise<SendGroupDataResponse> => {
    const url = this.URL('/api/v1/market-alerts/list');
    return this.get(url);
  };

  public markReadUnread = (
    params: NotificationMarkReadUnreadParams,
  ): SafePromise<NotificationMarkReadUnreadResponse> => {
    const url = this.URL(`/api/v1.1/notification/status`);
    return this.post(url, params);
  };

  updateNotificationRead = (params: NotificationReadUpdateParams): SafePromise<NotificationHistoryResponse> => {
    const url = this.URL(`/api/v1.1/notification/status`, params);
    return this.post(url, {
      bz_uid: params.bz_uid,
      read_all: params.read_all,
      read_ids: params.read_ids,
      unread_ids: params.unread_ids,
    });
  };

  public registerPushDevice = (params: NotificationRegisterParams): SafePromise<NotificationRegisterResponse> => {
    const url = this.URL(`${this.session.getEnvironment(NotificationEnvironment).webPushRoot}/register`);
    return this.post(url, params, { allowsAnonymousAuth: true });
  };

  public unregisterPushDevice = (params: { push: PushSubscription }): SafePromise<NotificationRegisterResponse> => {
    const url = this.URL(`${this.session.getEnvironment(NotificationEnvironment).webPushRoot}/unregister`);
    return this.post(url, params, { allowsAnonymousAuth: true });
  };
}
